import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Depenses from './pages/Depenses';
import Employees from './pages/Employees';
import Sales from './pages/Sales';
import Purchases from './pages/Purchases';
import Stores from './pages/Stores';
import Products from './pages/Products';
import Stickers from './pages/Stickers';
import Employes from './pages/Employes';
import Pointage from './pages/Pointage';
import Privileges from './pages/Privileges';
import AchatsReceptionCommandes from './pages/AchatsReceptionCommandes';
import AchatsAvoirs from './pages/AchatsAvoirs';
import AchatsFacturesFournisseurs from './pages/AchatsFacturesFournisseurs';
import Suppliers from './pages/Suppliers';
import PointsDeVenteFactures from './pages/PointsDeVenteFactures';
import VentesNouveau from './pages/VentesNouveau';
import Clients from './pages/Clients';
import Promotions from './pages/Promotions';
import Credits from './pages/Credits';
import Medecins from './pages/Medecins';
import VentesCommandes from './pages/VentesCommandes';
import VentesCommandesSupprimees from './pages/VentesCommandesSupprimees';
import VentesDevis from './pages/VentesDevis';
import VentesJournalLivraisons from './pages/VentesJournalLivraisons';
import VentesFactures from './pages/VentesFactures';
import AdminStores from './pages/AdminStores';
import Paiements from './pages/Paiements';
import PaiementsVueDEnsemble from './pages/PaiementsVueDEnsemble';
import PaiementsHistorique from './pages/PaiementsHistorique';
import GestionCheques from './pages/GestionCheques';
import Rapport from './pages/Rapport';
import StockMovements from './pages/StockMovements';
import PagePrivileges from './pages/PagePrivileges';
import Convention from './pages/Convention';
import EmployeeContacts from './pages/EmployeeContacts';
import Todos from './pages/Todos';
import WhatsApp from './pages/WhatsApp';
import WhatsAppConfig from './pages/WhatsAppConfig';
import GoogleSheetPage from './pages/GoogleSheetPage';
import Notes from './pages/Notes';
import { ModernLoadingSpinner } from './components/ui/ModernComponents';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // Log error if needed
  }
  render() {
    if (this.state.hasError) {
      return <div className="flex flex-col items-center justify-center min-h-screen"><h1 className="text-2xl font-bold text-red-600 mb-4">Une erreur inattendue est survenue.</h1><button className="btn btn-primary" onClick={() => window.location.reload()}>Recharger la page</button></div>;
    }
    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isEmployee, checkInRequired, checkInStatusLoading } = useAuth();
  const location = useLocation();

  if (loading || checkInStatusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const mustGoToPointage = isEmployee() && checkInRequired && location.pathname !== '/pointage';
  if (mustGoToPointage) {
    return <Navigate to="/pointage" replace />;
  }

  return children;
};

// Main App Component
const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><ModernLoadingSpinner size="lg" /></div>}>
        <Router>
          <Routes>
            <Route path="/depenses" element={<ProtectedRoute><Layout><Depenses /></Layout></ProtectedRoute>} />
            <Route 
              path="/login" 
              element={isAuthenticated() ? <Navigate to="/" replace /> : <Login />} 
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Products />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stickers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Stickers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sales />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchases"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Purchases />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stores"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Stores />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/employes" element={<ProtectedRoute><Layout><Employes /></Layout></ProtectedRoute>} />
            <Route path="/pointage" element={<ProtectedRoute><Layout><Pointage /></Layout></ProtectedRoute>} />
            <Route path="/privileges" element={<ProtectedRoute><Layout><Privileges /></Layout></ProtectedRoute>} />
            <Route path="/achats/reception-commandes" element={<ProtectedRoute><Layout><AchatsReceptionCommandes /></Layout></ProtectedRoute>} />
            <Route path="/achats/avoirs" element={<ProtectedRoute><Layout><AchatsAvoirs /></Layout></ProtectedRoute>} />
            <Route path="/achats/factures-fournisseurs" element={<ProtectedRoute><Layout><AchatsFacturesFournisseurs /></Layout></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><Layout><Suppliers /></Layout></ProtectedRoute>} />
            <Route path="/points-de-vente/factures" element={<ProtectedRoute><Layout><PointsDeVenteFactures /></Layout></ProtectedRoute>} />
            <Route path="/ventes/nouveau" element={<ProtectedRoute><Layout><VentesNouveau /></Layout></ProtectedRoute>} />
            <Route path="/clients" element={<ProtectedRoute><Layout><Clients /></Layout></ProtectedRoute>} />
            <Route path="/promotions" element={<ProtectedRoute><Layout><Promotions /></Layout></ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute><Layout><Credits /></Layout></ProtectedRoute>} />
            <Route path="/medecins" element={<ProtectedRoute><Layout><Medecins /></Layout></ProtectedRoute>} />
            <Route path="/ventes/commandes" element={<ProtectedRoute><Layout><VentesCommandes /></Layout></ProtectedRoute>} />
            <Route path="/ventes/commandes-supprimees" element={<ProtectedRoute><Layout><VentesCommandesSupprimees /></Layout></ProtectedRoute>} />
            <Route path="/ventes/devis" element={<ProtectedRoute><Layout><VentesDevis /></Layout></ProtectedRoute>} />
            <Route path="/ventes/journal-livraisons" element={<ProtectedRoute><Layout><VentesJournalLivraisons /></Layout></ProtectedRoute>} />
            <Route path="/ventes/factures" element={<ProtectedRoute><Layout><VentesFactures /></Layout></ProtectedRoute>} />
            <Route path="/admin/stores" element={<ProtectedRoute><Layout><AdminStores /></Layout></ProtectedRoute>} />
            <Route path="/paiements" element={<ProtectedRoute><Layout><Paiements /></Layout></ProtectedRoute>} />
            <Route path="/paiements/vue-d-ensemble" element={<ProtectedRoute><Layout><PaiementsVueDEnsemble /></Layout></ProtectedRoute>} />
            <Route path="/paiements/historique" element={<ProtectedRoute><Layout><PaiementsHistorique /></Layout></ProtectedRoute>} />
            <Route path="/gestion-cheques" element={<ProtectedRoute><Layout><GestionCheques /></Layout></ProtectedRoute>} />
            <Route path="/rapport" element={<ProtectedRoute><Layout><Rapport /></Layout></ProtectedRoute>} />
            <Route path="/stock-movements" element={<ProtectedRoute><Layout><StockMovements /></Layout></ProtectedRoute>} />
            <Route path="/page-privileges" element={<ProtectedRoute><Layout><PagePrivileges /></Layout></ProtectedRoute>} />
            <Route path="/convention" element={<ProtectedRoute><Layout><Convention /></Layout></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><Layout><EmployeeContacts /></Layout></ProtectedRoute>} />
            <Route path="/todos" element={<ProtectedRoute><Layout><Todos /></Layout></ProtectedRoute>} />
            <Route path="/notes" element={<ProtectedRoute><Layout><Notes /></Layout></ProtectedRoute>} />
            <Route path="/whatsapp" element={<ProtectedRoute><Layout><WhatsApp /></Layout></ProtectedRoute>} />
            <Route path="/whatsapp/config" element={<ProtectedRoute><Layout><WhatsAppConfig /></Layout></ProtectedRoute>} />
            <Route path="/feuille-google" element={<ProtectedRoute><Layout><GoogleSheetPage /></Layout></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </Suspense>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
};

// Root App Component
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
